import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Mail, Lock, User as UserIcon, KeyRound } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { loginWithToken } = useAuth();
  const initialMode = new URLSearchParams(search).get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ email: "", password: "", name: "", promo_code: "", promo_password: "" });
  const [loading, setLoading] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data } = await api.post("/auth/register", {
          email: form.email, password: form.password, name: form.name,
          promo_code: form.promo_code || undefined, promo_password: form.promo_password || undefined,
        });
        loginWithToken(data.token, data.user);
        toast.success(`Bienvenue ${data.user.name} !`);
        navigate("/dashboard");
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        loginWithToken(data.token, data.user);
        toast.success("Connecté");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Sparkles className="w-5 h-5 text-gold" />
          <span className="font-heading text-2xl">Xook<span className="gold-text font-bold">Pub</span></span>
        </Link>

        <div className="gold-border-gradient p-8">
          <h1 className="font-heading text-3xl tracking-tight">
            {mode === "signup" ? "Créer un compte" : "Connexion"}
          </h1>
          <p className="text-white/60 mt-2 text-sm">{mode === "signup" ? "Commencez votre indépendance créative." : "Heureux de vous revoir."}</p>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="mt-6 w-full flex items-center justify-center gap-3 py-3 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#fff" d="M21.6 12.227c0-.815-.073-1.6-.21-2.355H12v4.46h5.394a4.61 4.61 0 0 1-2 3.025v2.512h3.235c1.894-1.743 2.97-4.31 2.97-7.642Z"/><path fill="#fff" d="M12 22c2.7 0 4.965-.895 6.62-2.43l-3.235-2.513c-.897.605-2.045.962-3.385.962-2.605 0-4.81-1.76-5.6-4.124H3.06v2.595A9.997 9.997 0 0 0 12 22Z"/><path fill="#fff" d="M6.4 13.895a6.005 6.005 0 0 1 0-3.79V7.51H3.06a10.003 10.003 0 0 0 0 8.98l3.34-2.595Z"/><path fill="#fff" d="M12 5.98c1.47 0 2.787.504 3.825 1.495l2.868-2.867C16.96 3.072 14.695 2 12 2A9.997 9.997 0 0 0 3.06 7.51l3.34 2.595C7.19 7.74 9.395 5.98 12 5.98Z"/></svg>
            Continuer avec Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-white/40">
            <div className="flex-1 h-px bg-white/10" />
            OU
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <label className="block">
                <span className="label-mono">Nom complet</span>
                <div className="mt-1 flex items-center gap-2 bg-obsidian-light border border-white/10 rounded-lg px-3 focus-within:border-gold/50">
                  <UserIcon className="w-4 h-4 text-white/40" />
                  <input data-testid="signup-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="bg-transparent flex-1 py-3 outline-none text-sm" placeholder="Jean Dupont" />
                </div>
              </label>
            )}
            <label className="block">
              <span className="label-mono">Email</span>
              <div className="mt-1 flex items-center gap-2 bg-obsidian-light border border-white/10 rounded-lg px-3 focus-within:border-gold/50">
                <Mail className="w-4 h-4 text-white/40" />
                <input data-testid="auth-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="bg-transparent flex-1 py-3 outline-none text-sm" placeholder="vous@email.com" />
              </div>
            </label>
            <label className="block">
              <span className="label-mono">Mot de passe</span>
              <div className="mt-1 flex items-center gap-2 bg-obsidian-light border border-white/10 rounded-lg px-3 focus-within:border-gold/50">
                <Lock className="w-4 h-4 text-white/40" />
                <input data-testid="auth-password" type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className="bg-transparent flex-1 py-3 outline-none text-sm" placeholder="••••••••" />
              </div>
            </label>

            {mode === "signup" && (
              <div className="pt-2">
                <button type="button" onClick={()=>setShowPromo(!showPromo)} data-testid="toggle-promo" className="text-xs text-gold/80 hover:text-gold inline-flex items-center gap-1">
                  <KeyRound className="w-3.5 h-3.5" /> {showPromo ? "Masquer" : "J'ai un code promo (accès gratuit)"}
                </button>
                {showPromo && (
                  <div className="mt-3 space-y-2 p-4 rounded-lg bg-gold/5 border border-gold/20">
                    <input data-testid="promo-code" value={form.promo_code} onChange={(e)=>setForm({...form, promo_code:e.target.value})} placeholder="Code promo (XOOKPUB-OR-2026)" className="w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/50" />
                    <input data-testid="promo-password" type="password" value={form.promo_password} onChange={(e)=>setForm({...form, promo_password:e.target.value})} placeholder="Mot de passe promo" className="w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/50" />
                  </div>
                )}
              </div>
            )}

            <button data-testid="auth-submit" type="submit" disabled={loading} className="gold-btn w-full !py-3 mt-4 disabled:opacity-50">
              {loading ? "..." : (mode === "signup" ? "Créer mon compte" : "Se connecter")}
            </button>
          </form>

          {mode === "login" && (
            <Link to="/forgot-password" data-testid="forgot-password-link" className="mt-4 block text-xs text-white/60 hover:text-gold text-center">
              Mot de passe oublié ?
            </Link>
          )}

          <button
            data-testid="auth-toggle-mode"
            onClick={()=>setMode(mode === "signup" ? "login" : "signup")}
            className="mt-6 text-sm text-white/60 hover:text-gold w-full text-center"
          >
            {mode === "signup" ? "Déjà un compte ? Se connecter" : "Pas encore inscrit ? Créer un compte"}
          </button>
        </div>
      </div>
    </div>
  );
}
